import { lazy } from 'react';
import {
  IconBook,
  IconGift,
  IconHelpCircle,
  IconLayoutList,
  IconSchool,
  IconTicket,
  IconUsers,
  IconWriting,
} from '@tabler/icons-react';
import type { AppRuntime } from '@ayocore/platform/client/runtime/AppRuntime';

import moduleInfo from '../module.json';
import { ACADEMY_ROLES } from '../common/roles';
import { registerAcademyPartnerViewExtensions } from './extensions/AcademyPartnerViewExtension';
import { hasAnyAcademyRole } from './guards';

/**
 * Регистрация клиентской части модуля academy — внутренняя админка контента
 * в панели Ayo (модули, уроки, экзамены, призы, заявки, FAQ).
 *
 * Внешний кабинет продавца/дилера — отдельное приложение (`apps/academy_portal`)
 * и к этому bootstrap отношения не имеет.
 *
 * Все пункты собраны в одну группу «Академия» и закрыты guard'ами по ролям:
 * пункт показывается только той роли, которой сервер разрешит работу с
 * соответствующим репозиторием, иначе клик приводил бы к 403.
 */
export async function setup(app: AppRuntime): Promise<void> {
  // Вкладка «Академия» в карточке партнёра CRM — активация дилера
  registerAcademyPartnerViewExtensions();

  const academy = app.useModule(moduleInfo.name, {
    version: moduleInfo.version,
    description: moduleInfo.description,
    navigationOrder: 25,
  });

  const groupAcademy = academy.createNavigationGroup({
    key: 'academy',
    label: 'Академия',
    icon: IconSchool,
    order: 1,
  });

  academy.useNavigation([
    {
      group: groupAcademy,
      icon: IconLayoutList,
      label: 'Разделы',
      url: '/academy/sections',
      component: lazy(() => import('./components/Sections/SectionsList')),
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
    {
      group: groupAcademy,
      icon: IconBook,
      label: 'Модули',
      url: '/academy/modules',
      component: lazy(() => import('./components/Modules/ModulesList')),
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
    {
      icon: IconBook,
      label: 'Модуль',
      url: '/academy/modules/:id',
      component: lazy(() => import('./components/Modules/ModuleView')),
      hiddenFromMenu: true,
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
    {
      icon: IconBook,
      label: 'Новый урок',
      url: '/academy/modules/:moduleId/lessons/new',
      component: lazy(() => import('./components/Lessons/LessonView')),
      hiddenFromMenu: true,
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
    {
      icon: IconBook,
      label: 'Урок',
      url: '/academy/lessons/:id',
      component: lazy(() => import('./components/Lessons/LessonView')),
      hiddenFromMenu: true,
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
    {
      group: groupAcademy,
      icon: IconWriting,
      label: 'Экзамены',
      url: '/academy/exams',
      component: lazy(() => import('./components/Exams/ExamsList')),
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
    {
      icon: IconWriting,
      label: 'Экзамен',
      url: '/academy/exams/:id',
      component: lazy(() => import('./components/Exams/ExamView')),
      hiddenFromMenu: true,
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
    {
      group: groupAcademy,
      icon: IconGift,
      label: 'Призы',
      url: '/academy/rewards',
      component: lazy(() => import('./components/Rewards/RewardsList')),
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
    {
      icon: IconGift,
      label: 'Приз',
      url: '/academy/rewards/:id',
      component: lazy(() => import('./components/Rewards/RewardView')),
      hiddenFromMenu: true,
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
    {
      group: groupAcademy,
      icon: IconTicket,
      label: 'Заявки на призы',
      url: '/academy/redemptions',
      component: lazy(() => import('./components/Redemptions/RedemptionsList')),
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_MODERATOR),
    },
    {
      group: groupAcademy,
      icon: IconUsers,
      label: 'Участники',
      url: '/academy/participants',
      // Именованный экспорт (CODING_RULES.MD) — lazy() ждёт модуль с default.
      component: lazy(() =>
        import('./components/Participants/ParticipantsList').then(m => ({
          default: m.ParticipantsList,
        }))
      ),
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_MODERATOR),
    },
    {
      icon: IconUsers,
      label: 'Участник',
      url: '/academy/participants/:id',
      component: lazy(() =>
        import('./components/Participants/ParticipantView').then(m => ({
          default: m.ParticipantView,
        }))
      ),
      hiddenFromMenu: true,
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_MODERATOR),
    },
    {
      group: groupAcademy,
      icon: IconHelpCircle,
      label: 'FAQ',
      url: '/academy/faq',
      component: lazy(() => import('./components/Faq/FaqList')),
      guard: hasAnyAcademyRole(ACADEMY_ROLES.ACADEMY_ADMIN, ACADEMY_ROLES.ACADEMY_CONTENT),
    },
  ]);
}
